/**
 * Utility functions for parsing and building audience criterion JSON
 * Used by AudienceDetail.vue and AddAudience.vue
 */

/**
 * Parse a single criterion node into the UI format
 * @param {Object} c - Criterion object with column_name, operator, values
 * @returns {Object} UI criterion format
 */
export function parseSingleCriterion(c) {
  const column = c.column_name || c.column
  const operator = (c.operator || 'in').toUpperCase()

  if (operator === 'BETWEEN') {
    return {
      column,
      condition: 'BETWEEN',
      values: [],
      lowerbound: c.values?.[0] || null,
      upperbound: c.values?.[1] || null,
      singleValue: null,
    }
  } else if (operator === 'IN' && c.values?.length === 1) {
    return {
      column,
      condition: 'EQUAL',
      values: [],
      lowerbound: null,
      upperbound: null,
      singleValue: c.values[0],
    }
  } else {
    return {
      column,
      condition: 'IN',
      values: c.values || [],
      lowerbound: null,
      upperbound: null,
      singleValue: null,
    }
  }
}

/**
 * Recursively flatten nested criterion structure to extract all leaf criteria
 * @param {Object} criterion - Nested criterion object
 * @returns {Array} Array of parsed leaf criteria
 */
export function flattenCriteriaToLeaves(criterion) {
  const leaves = []

  const traverse = (node) => {
    if (!node) return

    // If it's a leaf criterion (has column_name), add it
    if (node.column_name || node.column) {
      leaves.push(parseSingleCriterion(node))
    } else if (node.operands && Array.isArray(node.operands)) {
      // Recursively traverse operands
      node.operands.forEach(op => traverse(op))
    } else if (node.operand) {
      // Handle 'not' operator with single operand
      traverse(node.operand)
    }
  }

  traverse(criterion)
  return leaves
}

/**
 * Parse criterion JSON into simple criteria format (flat list)
 * @param {Object} criterion - Criterion object
 * @returns {Array} Array of simple criteria {column, values}
 */
export function parseSimpleCriteria(criterion) {
  if (!criterion) return [{ column: null, values: [] }]

  // Single criterion
  if (criterion.column_name || criterion.column) {
    return [{
      column: criterion.column_name || criterion.column,
      values: criterion.values || []
    }]
  }

  // For nested structures, flatten all leaf criteria
  if (criterion.operands && Array.isArray(criterion.operands)) {
    const leaves = []
    const extractLeaves = (node) => {
      if (!node) return
      if (node.column_name || node.column) {
        leaves.push({
          column: node.column_name || node.column,
          values: node.values || []
        })
      } else if (node.operands && Array.isArray(node.operands)) {
        node.operands.forEach(op => extractLeaves(op))
      } else if (node.operand) {
        extractLeaves(node.operand)
      }
    }
    criterion.operands.forEach(op => extractLeaves(op))
    return leaves.length > 0 ? leaves : [{ column: null, values: [] }]
  }

  return [{ column: null, values: [] }]
}

/**
 * Parse criterion JSON into advanced criteria format (groups)
 * Preserves nested structures recursively
 * @param {Object} criterion - Criterion object
 * @returns {Object} { operator, groups } where groups is array of criterion groups
 */
export function parseAdvancedCriteria(criterion) {
  const defaultGroup = {
    operator: 'AND',
    criteria: [{ column: null, condition: 'IN', values: [], lowerbound: null, upperbound: null, singleValue: null }],
    groups: []
  }

  if (!criterion) {
    return { operator: 'OR', negated: false, groups: [defaultGroup] }
  }

  // Determine top-level operator
  const operator = (criterion.operator || 'or').toUpperCase()

  // Parse groups - preserve nested structures recursively
  let groups
  if (criterion.operands && Array.isArray(criterion.operands)) {
    groups = criterion.operands.map(group => parseCriterionGroup(group))
  } else {
    // Single criterion
    groups = [{
      operator: 'AND',
      criteria: [parseSingleCriterion(criterion)],
      groups: []
    }]
  }

  return { operator, groups }
}

/**
 * Parse a single criterion group (can be leaf criterion or nested structure)
 * @param {Object} group - Criterion group object
 * @returns {Object} Parsed group with criteria and nested groups
 */
function parseCriterionGroup(group) {
  // If it's a leaf criterion (has column_name directly)
  if (group.column_name || group.column) {
    return {
      operator: 'AND',
      criteria: [parseSingleCriterion(group)],
      groups: []
    }
  }

  // If it has operands, it's a nested structure
  if (group.operands && Array.isArray(group.operands)) {
    const operator = (group.operator || 'and').toUpperCase()
    const criteria = []
    const nestedGroups = []

    // Separate leaf criteria from nested groups
    group.operands.forEach(operand => {
      if (operand.column_name || operand.column) {
        // Leaf criterion
        criteria.push(parseSingleCriterion(operand))
      } else if (operand.operands && Array.isArray(operand.operands)) {
        // Nested group - recursively parse it
        nestedGroups.push(parseCriterionGroup(operand))
      } else {
        // Single criterion
        criteria.push(parseSingleCriterion(operand))
      }
    })

    return {
      operator,
      // Only add empty criterion if there are no nested groups AND no criteria
      // If there are nested groups, we don't need an empty criterion placeholder
      criteria: criteria.length > 0 ? criteria : (nestedGroups.length === 0 ? [{ column: null, condition: 'IN', values: [], lowerbound: null, upperbound: null, singleValue: null }] : []),
      groups: nestedGroups
    }
  }

  // Fallback: single criterion
  return {
    operator: 'AND',
    criteria: [parseSingleCriterion(group)],
    groups: []
  }
}

/**
 * Check if a criterion structure is "simple" (can be represented as flat AND list)
 * @param {Object} criterion - Criterion object
 * @returns {boolean}
 */
export function isSimpleCriterion(criterion) {
  if (!criterion) return true
  // Simple if: single operand OR (and/or with flat operands, no nested groups)
  if (criterion.column_name || criterion.column) return true
  if ((criterion.operator === 'and' || criterion.operator === 'or') && criterion.operands) {
    return !criterion.operands.some(op => op.operands || op.operator === 'not')
  }
  return false
}

/**
 * Calculate the nesting depth of a criterion structure
 * @param {Object} criterion - Criterion object
 * @param {number} currentDepth - Current depth (for recursion)
 * @returns {number} Maximum nesting depth
 */
export function getCriterionNestingDepth(criterion, currentDepth = 0) {
  if (!criterion) return currentDepth

  // Leaf node (has column_name)
  if (criterion.column_name || criterion.column) {
    return currentDepth
  }

  let maxChildDepth = currentDepth

  if (criterion.operands && Array.isArray(criterion.operands)) {
    criterion.operands.forEach(op => {
      const childDepth = getCriterionNestingDepth(op, currentDepth + 1)
      maxChildDepth = Math.max(maxChildDepth, childDepth)
    })
  }

  if (criterion.operand) {
    const childDepth = getCriterionNestingDepth(criterion.operand, currentDepth + 1)
    maxChildDepth = Math.max(maxChildDepth, childDepth)
  }

  return maxChildDepth
}

/**
 * Build criterion JSON from simple criteria
 * @param {Array} criteria - Array of {column, values}
 * @returns {Object} Criterion JSON object
 */
export function buildSimpleCriterionJson(criteria) {
  const operands = criteria
    .filter(c => c.column && c.values && c.values.length > 0)
    .map(c => ({
      column_name: c.column,
      operator: 'in',
      values: c.values,
    }))

  if (operands.length === 0) {
    return { criterion: {} }
  }

  const criterion = operands.length === 1 ? operands[0] : {
    operator: 'and',
    operands
  }

  return { criterion }
}

/**
 * Build criterion JSON from advanced criteria groups
 * Recursively builds nested structures
 * @param {Array} groups - Array of criterion groups
 * @param {Object} options - { operator: 'AND'|'OR', negated: boolean }
 * @returns {Object} Criterion JSON object
 */
export function buildAdvancedCriterionJson(groups, options = {}) {
  const { operator = 'OR', negated = false } = options

  const groupOperands = groups.map(group => buildCriterionGroupJson(group)).filter(g => g)

  if (groupOperands.length === 0) {
    return { criterion: {} }
  }

  let criterion
  if (groupOperands.length === 1) {
    criterion = groupOperands[0]
  } else {
    criterion = {
      operator: operator.toLowerCase(),
      operands: groupOperands,
    }
  }

  if (negated) {
    criterion = {
      operator: 'not',
      operand: criterion,
    }
  }

  return { criterion }
}

/**
 * Build JSON for a single criterion group (handles nested groups recursively)
 * @param {Object} group - Criterion group with criteria and optional nested groups
 * @returns {Object} Criterion JSON object for this group
 */
function buildCriterionGroupJson(group) {
  // Build leaf criteria operands
  const criteriaOperands = (group.criteria || [])
    .filter(c => c.column)
    .map(c => {
      if (c.condition === 'IN') {
        return {
          column_name: c.column,
          operator: 'in',
          values: c.values || [],
        }
      } else if (c.condition === 'BETWEEN') {
        return {
          column_name: c.column,
          operator: 'between',
          values: [c.lowerbound, c.upperbound],
        }
      } else if (c.condition === 'EQUAL') {
        return {
          column_name: c.column,
          operator: 'in',
          values: [c.singleValue],
        }
      }
      return null
    })
    .filter(c => c)

  // Build nested groups operands
  const nestedGroupOperands = (group.groups || [])
    .map(nestedGroup => buildCriterionGroupJson(nestedGroup))
    .filter(g => g)

  // Combine all operands
  const allOperands = [...criteriaOperands, ...nestedGroupOperands]

  if (allOperands.length === 0) {
    return null
  }

  // Always preserve the nested structure with operator and operands array
  // This ensures single-operand nested structures (like c2999) are preserved
  // as they were in the original JSON
  return {
    operator: (group.operator || 'and').toLowerCase(),
    operands: allOperands,
  }
}

/**
 * Pre-populate column values object from simple criteria
 * @param {Array} criteria - Array of {column, values}
 * @param {Object} existingValues - Existing columnValues object
 * @returns {Object} Updated columnValues object
 */
export function prePopulateColumnValues(criteria, existingValues = {}) {
  const newColumnValues = { ...existingValues }
  criteria.forEach(c => {
    if (c.column && c.values && c.values.length > 0) {
      if (!newColumnValues[c.column]) {
        newColumnValues[c.column] = [...c.values]
      } else {
        newColumnValues[c.column] = [...new Set([...newColumnValues[c.column], ...c.values])]
      }
    }
  })
  return newColumnValues
}

/**
 * Pre-populate column values object from advanced criteria groups
 * Recursively handles nested groups
 * @param {Array} groups - Array of criterion groups
 * @param {Object} existingValues - Existing columnValues object
 * @returns {Object} Updated columnValues object
 */
export function prePopulateColumnValuesFromGroups(groups, existingValues = {}) {
  const newColumnValues = { ...existingValues }
  
  const processGroup = (group) => {
    // Process leaf criteria in this group
    if (group.criteria) {
      group.criteria.forEach(c => {
        if (c.column) {
          let values = []
          if (c.condition === 'IN' && c.values && c.values.length > 0) {
            values = c.values
          } else if (c.condition === 'BETWEEN') {
            if (c.lowerbound) values.push(c.lowerbound)
            if (c.upperbound) values.push(c.upperbound)
          } else if (c.condition === 'EQUAL' && c.singleValue) {
            values = [c.singleValue]
          }
          if (values.length > 0) {
            if (!newColumnValues[c.column]) {
              newColumnValues[c.column] = [...values]
            } else {
              newColumnValues[c.column] = [...new Set([...newColumnValues[c.column], ...values])]
            }
          }
        }
      })
    }
    
    // Recursively process nested groups
    if (group.groups) {
      group.groups.forEach(nestedGroup => processGroup(nestedGroup))
    }
  }
  
  groups.forEach(group => processGroup(group))
  return newColumnValues
}

/**
 * Extract values from histogram API response
 * @param {Object} result - API response
 * @returns {Array} Array of values
 */
export function extractValuesFromHistogram(result) {
  // Audsum format: { data: { header: ["attribute_name", "attribute_value", "count"], body: [["col", "val", 123]] } }
  if (result?.data?.body && Array.isArray(result.data.body)) {
    const header = result.data.header || []
    let valueIndex = header.indexOf('attribute_value')
    if (valueIndex === -1) valueIndex = 1  // Default to second column

    return result.data.body
      .map(row => row[valueIndex])
      .filter(v => v != null && v !== '' && v !== '-')
  }

  // Legacy formats
  if (result?.response && Array.isArray(result.response)) {
    return result.response.map(item => item.value || item[0]).filter(v => v)
  }
  if (Array.isArray(result?.data)) {
    return result.data.map(item => item.value || item[0]).filter(v => v)
  }

  return []
}

/**
 * Create empty criterion group for advanced mode
 * @returns {Object} Empty criterion group
 */
export function createEmptyCriterionGroup() {
  return {
    operator: 'AND',
    criteria: [{
      column: null,
      condition: 'IN',
      values: [],
      lowerbound: null,
      upperbound: null,
      singleValue: null,
    }],
    groups: []
  }
}

/**
 * Create empty simple criterion
 * @returns {Object} Empty simple criterion
 */
export function createEmptySimpleCriterion() {
  return { column: null, values: [] }
}
